import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, Plus, Trash2, Save, Loader2, Home, Building2, Video } from 'lucide-react';
import { updateDoctor, updateDoctorSchedule, updateMySchedulingSettings, type DoctorDTO } from '@/services/doctors';
import {
  AVAILABILITY_TYPE_LABELS,
  getAvailabilityTypeLabel,
  normalizeAvailabilityType,
  type AvailabilityTypeValue,
} from '@/lib/uiLabels';
import { toast } from 'sonner';

interface DoctorAvailabilitySettingsProps {
  doctor: DoctorDTO;
  onUpdate: (updatedDoctor: DoctorDTO) => void;
}

const DAYS = [
  { id: 'monday', label: 'Segunda-feira' },
  { id: 'tuesday', label: 'Terça-feira' },
  { id: 'wednesday', label: 'Quarta-feira' },
  { id: 'thursday', label: 'Quinta-feira' },
  { id: 'friday', label: 'Sexta-feira' },
  { id: 'saturday', label: 'Sábado' },
  { id: 'sunday', label: 'Domingo' },
];

interface LocalScheduleBlock {
  startTime: string;
  endTime: string;
  type: AvailabilityTypeValue;
}

interface LocalSchedule {
  [key: string]: LocalScheduleBlock[];
}

export function DoctorAvailabilitySettings({ doctor, onUpdate }: DoctorAvailabilitySettingsProps) {
  const [duration, setDuration] = useState(doctor.consultationDuration || 30);
  const [schedule, setSchedule] = useState<LocalSchedule>({});
  const [minAdvanceTelemedicine, setMinAdvanceTelemedicine] = useState(Math.round((doctor.minAdvanceTelemedicineMinutes ?? 0) / 60));
  const [minAdvanceOffice, setMinAdvanceOffice] = useState(Math.round((doctor.minAdvanceOfficeMinutes ?? 1440) / 60));
  const [minAdvanceHomecare, setMinAdvanceHomecare] = useState(Math.round((doctor.minAdvanceHomecareMinutes ?? 1440) / 60));
  const [isSaving, setIsSaving] = useState(false);

  // Initialize schedule from doctor.availability
  useEffect(() => {
    const initialSchedule: LocalSchedule = {};
    DAYS.forEach(day => {
      initialSchedule[day.id] = (doctor.availability || [])
        .filter(b => b.dayOfWeek === day.id.toUpperCase())
        .map(b => ({ 
          startTime: b.startTime, 
          endTime: b.endTime, 
          type: normalizeAvailabilityType(b.type || 'CLINIC'),
        }));
    });
    setSchedule(initialSchedule);
  }, [doctor.availability]);

  const hasClinicAddress = Boolean(doctor.hasClinic && (doctor.clinicAddress || doctor.officeStreet));
  const modalityOptions: Array<{
    value: LocalScheduleBlock['type'];
    label: string;
    icon: typeof Video;
  }> = [
    { value: 'TELEMEDICINE', label: 'Telemedicina', icon: Video },
    { value: 'HOME', label: 'Domiciliar', icon: Home },
    ...(hasClinicAddress ? [{ value: 'CLINIC' as const, label: 'Consultório', icon: Building2 }] : []),
  ];

  const handleAddBlock = (day: string) => {
    const currentDaySchedule = schedule[day] || [];
    setSchedule({
      ...schedule,
      [day]: [...currentDaySchedule, { startTime: '08:00', endTime: '12:00', type: modalityOptions[0].value }],
    });
  };

  const handleRemoveBlock = (day: string, index: number) => {
    const currentDaySchedule = [...(schedule[day] || [])];
    currentDaySchedule.splice(index, 1);
    setSchedule({
      ...schedule,
      [day]: currentDaySchedule,
    });
  };

  const handleUpdateBlock = (day: string, index: number, field: keyof LocalScheduleBlock, value: string) => {
    const currentDaySchedule = [...(schedule[day] || [])];
    currentDaySchedule[index] = { ...currentDaySchedule[index], [field]: value };
    setSchedule({
      ...schedule,
      [day]: currentDaySchedule,
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Update duration
      const docRes = await updateDoctor(doctor.id, { consultationDuration: duration });
      
      // Map local state to availability array for API
      const availabilityPayload = Object.entries(schedule).flatMap(([day, blocks]) =>
        blocks.map((block) => ({
          dayOfWeek: day.toUpperCase() as any,
          startTime: block.startTime,
          endTime: block.endTime,
          type: block.type
        }))
      );

      // Update schedule
      const schedRes = await updateDoctorSchedule(doctor.id, { availability: availabilityPayload });

      // Update scheduling settings (min advance)
      const settingsRes = await updateMySchedulingSettings({
        minAdvanceTelemedicineMinutes: minAdvanceTelemedicine * 60,
        minAdvanceOfficeMinutes: minAdvanceOffice * 60,
        minAdvanceHomecareMinutes: minAdvanceHomecare * 60,
      });

      if (docRes.success && schedRes.success && settingsRes.success && schedRes.data) {
        toast.success('Agenda e configurações atualizadas com sucesso!');
        onUpdate({
          ...schedRes.data,
          ...settingsRes.data,
        });
      } else {
        toast.error('Erro ao atualizar agenda.');
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Erro ao salvar alterações.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border-gray-200 dark:border-neutral-800 shadow-xs">
      <CardHeader className="border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/30">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-teal-600" />
          <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
            Grade de Horários Semanais
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-8">
        <div className="flex flex-col md:flex-row md:items-center gap-6 p-4 bg-teal-50/50 dark:bg-teal-900/10 rounded-xl border border-teal-100 dark:border-teal-900/30">
          <div className="flex-1 space-y-0.5">
            <Label className="text-sm font-bold text-gray-900 dark:text-white">
              Duração da Consulta
            </Label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Define o intervalo de cada slot na sua agenda.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-24 bg-white dark:bg-neutral-900"
              min={10}
              step={5}
            />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">minutos</span>
          </div>
        </div>

        <div className="p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30 space-y-4">
          <div className="space-y-0.5">
            <Label className="text-sm font-bold text-gray-900 dark:text-white">
              Antecedência Mínima para Agendamento
            </Label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Define com quantas horas de antecedência o paciente pode marcar uma consulta. Use 0 para permitir agendamentos imediatos.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                <Video className="w-3.5 h-3.5" /> Telemedicina (horas)
              </Label>
              <Input
                type="number"
                value={minAdvanceTelemedicine}
                onChange={(e) => setMinAdvanceTelemedicine(Number(e.target.value))}
                className="bg-white dark:bg-neutral-900"
                min={0}
                step={1}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" /> Consultório (horas)
              </Label>
              <Input
                type="number"
                value={minAdvanceOffice}
                onChange={(e) => setMinAdvanceOffice(Number(e.target.value))}
                className="bg-white dark:bg-neutral-900"
                min={0}
                step={1}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                <Home className="w-3.5 h-3.5" /> Domiciliar (horas)
              </Label>
              <Input
                type="number"
                value={minAdvanceHomecare}
                onChange={(e) => setMinAdvanceHomecare(Number(e.target.value))}
                className="bg-white dark:bg-neutral-900"
                min={0}
                step={1}
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {DAYS.map((day) => (
            <div key={day.id} className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  {day.label}
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 font-normal">
                    {(schedule[day.id] || []).length} blocos
                  </span>
                </h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleAddBlock(day.id)}
                  className="h-8 text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/20"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Bloco
                </Button>
              </div>

              <div className="space-y-2">
                {(schedule[day.id] || []).map((block, index) => (
                  <div 
                    key={index} 
                    className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 dark:bg-neutral-800/50 rounded-lg border border-gray-100 dark:border-neutral-800 animate-in fade-in zoom-in-95 duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <Input
                        type="time"
                        value={block.startTime}
                        onChange={(e) => handleUpdateBlock(day.id, index, 'startTime', e.target.value)}
                        className="w-32 h-9 bg-white dark:bg-neutral-900 text-xs"
                      />
                      <span className="text-gray-400 text-xs">até</span>
                      <Input
                        type="time"
                        value={block.endTime}
                        onChange={(e) => handleUpdateBlock(day.id, index, 'endTime', e.target.value)}
                        className="w-32 h-9 bg-white dark:bg-neutral-900 text-xs"
                      />
                    </div>

                    <div className="flex-1 min-w-[140px]">
                      <Select
                        value={normalizeAvailabilityType(block.type)}
                        onValueChange={(val) =>
                          handleUpdateBlock(
                            day.id,
                            index,
                            'type',
                            normalizeAvailabilityType(val),
                          )
                        }
                        items={AVAILABILITY_TYPE_LABELS}
                      >
                        <SelectTrigger className="h-9 bg-white dark:bg-neutral-900 text-xs">
                          <SelectValue placeholder="Tipo">
                            {(value) => getAvailabilityTypeLabel(value as string)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {modalityOptions.map((option) => {
                            const Icon = option.icon;
                            return (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                label={option.label}
                              >
                                <div className="flex items-center gap-2">
                                  <Icon className="w-3.5 h-3.5" />
                                  <span>{option.label}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {!hasClinicAddress && (
                        <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                          Cadastre um consultório para habilitar essa modalidade.
                        </p>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveBlock(day.id, index)}
                      className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}

                {(schedule[day.id] || []).length === 0 && (
                  <p className="text-xs text-gray-400 italic py-2">Nenhum horário configurado para este dia.</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="pt-4 border-t border-gray-100 dark:border-neutral-800">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold h-12 shadow-md shadow-teal-500/10"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Salvando Agenda...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Salvar Todas as Alterações
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
